import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import {
  Ban,
  Briefcase,
  Calendar,
  Mail,
  MapPin,
  Phone,
  Pencil,
  RotateCcw,
  Trash2,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import BackNavLink from "../../components/dashboard/BackNavLink";
import StatusBadge from "../../components/dashboard/StatusBadge";
import Button from "../../components/ui/Button";
import EditInstitutionModal from "../../components/dashboard/EditInstitutionModal";
import InstitutionActionModal from "../../components/dashboard/InstitutionActionModal";
import {
  getInstitutionById,
  subscribeInstitutions,
  updateInstitution,
} from "../../data/institutionsStore";
import { formatCurrency, formatDate } from "../../data/mock/dashboardData";
import {
  clearInstitutionsCache,
  deleteInstitution,
  fetchInstitution,
  INSTITUTION_STATUS_LABELS,
  updateInstitutionDetails,
  updateInstitutionStatus,
} from "../../services/institutionsApi";
import { USE_MOCK_AUTH } from "../../config/api";
import { resolveMediaUrl } from "../../utils/resolveMediaUrl";
import usePermissions from "../../hooks/usePermissions";
import useRole from "../../hooks/useRole";
import { OPS_PERMISSIONS as P } from "../../config/permissions";
import { navigateWithReturn } from "../../utils/navigationReturn";
import InstitutionOpsScopePanel from "../../components/dashboard/InstitutionOpsScopePanel";
import { showError, showSuccess } from "../../utils/toast";

function institutionStatusBadge(status) {
  const map = {
    active: { status: "active", label: INSTITUTION_STATUS_LABELS.active },
    inactive: { status: "inactive", label: INSTITUTION_STATUS_LABELS.inactive },
    deleted: { status: "deleted", label: INSTITUTION_STATUS_LABELS.deleted },
    onboarding: { status: "invited", label: INSTITUTION_STATUS_LABELS.onboarding },
  };
  const item = map[status] ?? { status: "open", label: status };
  return <StatusBadge status={item.status} label={item.label} />;
}

export default function InstitutionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const { hasPermission } = usePermissions();
  const { isSuperAdmin, isExecutiveManager } = useRole();
  const canUpdate = (isSuperAdmin || isExecutiveManager) && hasPermission(P.INSTITUTIONS_UPDATE);
  const canManageInstitutionScope = isSuperAdmin || isExecutiveManager;
  const [institution, setInstitution] = useState(() => (useLiveApi ? null : getInstitutionById(id)));
  const [loading, setLoading] = useState(useLiveApi);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const canMutateThis =
    canUpdate && (isSuperAdmin || (isExecutiveManager && institution?.canManage === true));

  useEffect(() => {
    if (useLiveApi) return undefined;
    setInstitution(getInstitutionById(id));
    return subscribeInstitutions(() => setInstitution(getInstitutionById(id)));
  }, [id, useLiveApi]);

  useEffect(() => {
    if (!useLiveApi) return undefined;
    let cancelled = false;

    async function loadInstitution() {
      setLoading(true);
      setLoadError(null);
      try {
        const result = await fetchInstitution(token, id);
        if (!cancelled) setInstitution(result);
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || "Could not load institution.");
          setInstitution(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadInstitution();
    return () => {
      cancelled = true;
    };
  }, [id, token, useLiveApi]);

  const refreshInstitution = async () => {
    const refreshed = await fetchInstitution(token, id);
    if (refreshed) setInstitution(refreshed);
    return refreshed;
  };

  const handleSave = async (changes) => {
    setSaving(true);
    try {
      await updateInstitutionDetails(token, id, changes);
      await refreshInstitution();
      setEditing(false);
      showSuccess("Institution updated successfully.");
    } catch (error) {
      showError(error.message || "Could not update institution.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    setSaving(true);
    try {
      const rowKey = institution?.id || institution?.provider_slug || id;
      if (statusAction === "remove") {
        const updated = await deleteInstitution(token, id);
        clearInstitutionsCache();
        updateInstitution(rowKey, {
          ...(updated || {}),
          status: "deleted",
          deleted_at: updated?.deleted_at || new Date().toISOString(),
        });
        setInstitution((prev) =>
          prev
            ? {
                ...prev,
                ...(updated || {}),
                status: "deleted",
                deleted_at: updated?.deleted_at || new Date().toISOString(),
              }
            : prev
        );
        showSuccess("Institution deleted.");
      } else if (statusAction === "deactivate") {
        const updated = await updateInstitutionStatus(token, id, "inactive");
        clearInstitutionsCache();
        updateInstitution(rowKey, { ...(updated || {}), status: "inactive", deleted_at: null });
        await refreshInstitution();
        showSuccess("Institution deactivated.");
      } else if (statusAction === "activate") {
        const updated = await updateInstitutionStatus(token, id, "active");
        clearInstitutionsCache();
        updateInstitution(rowKey, { ...(updated || {}), status: "active", deleted_at: null });
        await refreshInstitution();
        showSuccess("Institution activated.");
      } else if (statusAction === "restore") {
        const updated = await updateInstitutionStatus(token, id, "restore");
        clearInstitutionsCache();
        updateInstitution(rowKey, { ...(updated || {}), status: "active", deleted_at: null });
        await refreshInstitution();
        showSuccess("Institution restored.");
      }
    } catch (error) {
      showError(error.message || "Could not update institution.");
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const backLink = (
    <BackNavLink fallback="/dashboard/institutions" fallbackLabel="All institutions" />
  );

  if (loading && !institution) {
    return (
      <PageShell title="Institution" description="Loading institution…" actions={backLink}>
        <p className="text-sm text-primary/50">Loading institution details…</p>
      </PageShell>
    );
  }

  if (!institution) {
    return (
      <PageShell title="Institution" description="Institution not found." actions={backLink}>
        <p className="text-sm text-red-700">{loadError || `No institution matches provider slug ${id}.`}</p>
      </PageShell>
    );
  }

  const statusActionButton =
    institution.status === "deleted"
      ? {
          label: "Restore",
          Icon: RotateCcw,
          action: "restore",
          variant: "accent",
        }
      : institution.status === "active"
        ? {
            label: "Deactivate",
            Icon: Ban,
            action: "deactivate",
            variant: "outline",
          }
        : {
            label: "Activate",
            Icon: UserPlus,
            action: "activate",
            variant: "accent",
          };

  const StatusActionIcon = statusActionButton.Icon;

  return (
    <PageShell
      title={institution.name}
      description={[institution.type, institution.region, institution.city, institution.code]
        .filter(Boolean)
        .join(" · ")}
      actions={
        <div className="flex items-center gap-2">
          {canMutateThis ? (
            <>
              {institution.status !== "deleted" ? (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="h-4 w-4" />
                  Edit
                </Button>
              ) : null}
              <Button
                variant={statusActionButton.variant}
                size="sm"
                onClick={() => setStatusAction(statusActionButton.action)}
              >
                <StatusActionIcon className="h-4 w-4" />
                {statusActionButton.label}
              </Button>
              {institution.status !== "deleted" ? (
                <Button variant="outline" size="sm" onClick={() => setStatusAction("remove")}>
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              ) : null}
            </>
          ) : null}
          {backLink}
        </div>
      }
    >
      {institution.status === "inactive" && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
          <Ban className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <div>
            <p className="text-sm font-bold text-amber-900">Institution deactivated</p>
            <p className="mt-0.5 text-xs text-amber-800/80">
              Portal access is disabled. Activate to restore access.
            </p>
          </div>
        </div>
      )}

      {institution.status === "deleted" && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5">
          <Trash2 className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-red-900">Institution soft-deleted</p>
            <p className="mt-0.5 text-xs text-red-800/80">
              Portal login is blocked
              {institution.deleted_at ? ` · Since ${formatDate(institution.deleted_at)}` : ""}.
              Restore to clear deleted_at, set status active, and reopen provider access.
            </p>
          </div>
          {canMutateThis ? (
            <Button
              variant="accent"
              size="sm"
              className="shrink-0"
              onClick={() => setStatusAction("restore")}
            >
              <RotateCcw className="h-4 w-4" />
              Restore
            </Button>
          ) : null}
        </div>
      )}

      <div className="mb-6 overflow-hidden rounded-xl border border-primary/[0.08] bg-gradient-to-r from-primary/[0.05] via-white to-accent/[0.06] p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-primary text-lg font-bold text-white shadow-lg shadow-primary/25">
              {resolveMediaUrl(institution.logo) ? (
                <img
                  src={resolveMediaUrl(institution.logo)}
                  alt=""
                  className="box-border h-14 w-14 max-h-14 max-w-14 bg-white object-contain p-1.5"
                />
              ) : (
                (institution.code || institution.name || "?").slice(0, 2).toUpperCase()
              )}
            </span>
            <div>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {institutionStatusBadge(institution.status)}
              </div>
              <p className="text-sm text-primary/50">{institution.code}</p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-primary/45">
                <MapPin className="h-3.5 w-3.5" />
                {institution.gps_address || institution.address || "—"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigateWithReturn(navigate, `/dashboard/institutions/${id}/portfolio-groups`, location)
              }
            >
              <Briefcase className="h-4 w-4" />
              Portfolio groups
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigateWithReturn(navigate, `/dashboard/institutions/${id}/customers`, location)
              }
            >
              <Users className="h-4 w-4" />
              View customers
            </Button>
          </div>
        </div>

        <dl className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Metric icon={Briefcase} label="Active cases" value={String(institution.activeCases)} />
          <Metric icon={Wallet} label="Outstanding" value={formatCurrency(institution.outstanding)} />
          <Metric icon={Users} label="Portal login" value="1 account" />
        </dl>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <section className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)] lg:col-span-1">
          <h2 className="text-sm font-bold text-primary">Portal login</h2>
          <ul className="mt-4 space-y-3">
            <ContactRow icon={Mail} label="Primary email" value={institution.primary_email || institution.contactEmail} />
            <ContactRow icon={Phone} label="Primary phone" value={institution.primary_phone_number || institution.contactPhone} />
            <ContactRow icon={Mail} label="Contact email" value={institution.secondary_email || "—"} />
            <ContactRow icon={Phone} label="Contact phone" value={institution.secondary_phone_number || "—"} />
            <ContactRow icon={Calendar} label="Client since" value={formatDate(institution.since)} />
            <ContactRow icon={Calendar} label="Last login" value={formatDate(institution.last_login_at) || "—"} />
          </ul>
        </section>

        <section className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)] lg:col-span-1">
          <h2 className="text-sm font-bold text-primary">Institution details</h2>
          <dl className="mt-4 space-y-3">
            <ConfigRow label="Business type" value={institution.business_type || institution.type} />
            <ConfigRow label="Region" value={institution.region} />
            <ConfigRow label="City" value={institution.city || "—"} />
            <ConfigRow label="Website" value={institution.website || "—"} />
            <ChannelList channels={institution.channels} />
            <SocialList socials={institution.socials} />
          </dl>
        </section>

        <section className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-[0_1px_2px_rgba(127,29,29,0.04)] lg:col-span-1">
          <h2 className="text-sm font-bold text-primary">Caseload snapshot</h2>
          <p className="mt-1 text-xs text-primary/45">Counts for this institution only</p>
          <ul className="mt-4 space-y-2">
            <li className="flex items-center justify-between rounded-lg bg-primary/[0.02] px-3 py-2 text-xs">
              <span className="font-semibold text-primary">Active cases</span>
              <span className="tabular-nums text-primary/70">{institution.activeCases ?? 0}</span>
            </li>
            <li className="flex items-center justify-between rounded-lg bg-primary/[0.02] px-3 py-2 text-xs">
              <span className="font-semibold text-primary">Outstanding</span>
              <span className="tabular-nums text-primary/70">{formatCurrency(institution.outstanding)}</span>
            </li>
          </ul>
        </section>
      </div>

      {canManageInstitutionScope ? (
        <div className="mt-6">
          {!canMutateThis ? (
            <p className="mb-3 text-xs text-primary/50">
              View only. Edit, deactivate, delete, manager assignment, and SLA changes are available on institutions you onboarded.
            </p>
          ) : null}
          <InstitutionOpsScopePanel
            token={token}
            institutionId={institution.id || id}
            institutionName={institution.name}
            useLiveApi={useLiveApi}
            writable={canMutateThis}
          />
        </div>
      ) : null}

      {editing ? (
        <EditInstitutionModal
          institution={institution}
          saving={saving}
          onSave={handleSave}
          onClose={() => setEditing(false)}
        />
      ) : null}
      <InstitutionActionModal
        type={statusAction}
        institution={institution}
        loading={saving}
        onConfirm={handleStatusChange}
        onClose={() => setStatusAction(null)}
      />
    </PageShell>
  );
}

const SOCIAL_LABELS = {
  instagram: "Instagram",
  x: "X",
  twitter: "X",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  whatsapp: "WhatsApp",
};

const CHANNEL_LABELS = {
  sms: "SMS",
  whatsapp: "WhatsApp",
  email: "Email",
};

function ChannelList({ channels }) {
  const active = Object.entries(CHANNEL_LABELS).filter(([key]) => channels?.[key]);
  return (
    <div className="rounded-lg bg-primary/[0.02] px-3 py-2">
      <p className="text-xs text-primary/45">Channels</p>
      {active.length === 0 ? (
        <p className="mt-1 text-xs font-bold text-primary">—</p>
      ) : (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {active.map(([key, label]) => (
            <li
              key={key}
              className="rounded-md bg-white px-2 py-1 text-[11px] font-bold text-primary ring-1 ring-primary/10"
            >
              {label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SocialList({ socials }) {
  const links = Object.entries(socials || {}).filter(([, value]) => String(value || "").trim());
  return (
    <div className="rounded-lg bg-primary/[0.02] px-3 py-2">
      <p className="text-xs text-primary/45">Socials</p>
      {links.length === 0 ? (
        <p className="mt-1 text-xs font-bold text-primary">—</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {links.map(([key, url]) => {
            const href = String(url);
            const safe = /^https?:\/\//i.test(href) ? href : `https://${href}`;
            return (
              <li key={key} className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-primary/40">
                  {SOCIAL_LABELS[key] || key}
                </p>
                <a
                  href={safe}
                  target="_blank"
                  rel="noreferrer"
                  title={href}
                  className="block truncate text-xs font-semibold text-primary underline decoration-primary/20"
                >
                  {href}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-white/80 px-3.5 py-2.5 ring-1 ring-primary/[0.06]">
      <dt className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/40">
        <Icon className="h-3 w-3" />
        {label}
      </dt>
      <dd className="mt-1 text-sm font-bold tabular-nums text-primary">{value}</dd>
    </div>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/[0.06] text-primary/55">
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-primary/40">{label}</p>
        <p className="text-sm font-semibold text-primary">{value}</p>
      </div>
    </li>
  );
}

function ConfigRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-primary/[0.02] px-3 py-2">
      <dt className="text-xs text-primary/45">{label}</dt>
      <dd className="text-xs font-bold text-primary">{value}</dd>
    </div>
  );
}
